import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/vi';

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

type Props = {
    value?: string | Date | null; // nhận YYYY-MM-DD hoặc Date
    onChange: (ymd: string) => void; // trả YYYY-MM-DD hoặc ''
    placeholder?: string;
    disabled?: boolean;
    minDate?: string | Date | null;
    maxDate?: string | Date | null;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    sx?: any;
};

const toDayjs = (v?: string | Date | null): Dayjs | null => {
    if (!v) return null;
    const d = v instanceof Date ? dayjs(v) : dayjs(v);
    return d.isValid() ? d : null;
};

const toYmd = (d: Dayjs | null) => (d && d.isValid() ? d.format('YYYY-MM-DD') : '');

// ✅ FIX: header thứ trong tuần => T2..CN
const weekdayHeader = (date: Dayjs) => {
    // dayjs().day(): 0=CN, 1=T2, ..., 6=T7
    const d = date.day();
    if (d === 0) return 'CN';
    if (d === 6) return 'T7';
    return `T${d + 1}`; // 1->T2, 2->T3, ...
};

export default function AppDatePickerYMD({
    value,
    onChange,
    placeholder = 'dd/mm/yyyy',
    disabled,
    minDate,
    maxDate,
    size = 'small',
    fullWidth = true,
    sx,
}: Props) {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
            <DatePicker
                value={toDayjs(value)}
                onChange={(d) => onChange(toYmd(d))}
                format="DD/MM/YYYY"
                localeText={{todayButtonLabel:'Hôm nay',clearButtonLabel:'Xóa',cancelButtonLabel:'Hủy',okButtonLabel:'Chọn'}}
                views={['year', 'month', 'day']}
                disabled={disabled}
                minDate={toDayjs(minDate ?? null) ?? undefined}
                maxDate={toDayjs(maxDate ?? null) ?? undefined}
                dayOfWeekFormatter={weekdayHeader} // ✅ quan trọng
                slotProps={{
                    textField: {
                        size,
                        fullWidth,
                        placeholder,
                        className: 'app-date-time-field',
                        sx: {
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 12,
                                background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,251,253,0.96))',
                                minHeight: 42,
                            },
                            '& .MuiInputBase-input': {
                                paddingTop: 0,
                                paddingBottom: 0,
                                height: 42,
                                display: 'flex',
                                alignItems: 'center',
                                fontWeight: 600,
                            },
                            ...sx,
                        },
                    },
                    popper: {
                        className: 'ictu-datepicker-popup',
                        placement: 'bottom-start',
                        modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
                    },
                    desktopPaper: {
                        sx: {
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.10)',

                            '& .MuiPickersCalendarHeader-root': { px: 1.25, py: 1 },
                            '& .MuiPickersCalendarHeader-label': {
                                fontWeight: 900,
                                fontSize: 13,
                                color: '#123147',
                            },
                            '& .MuiPickersArrowSwitcher-root button': {
                                width: 32,
                                height: 32,
                                borderRadius: 999,
                            },

                            // ✅ header ngày trong tuần
                            '& .MuiDayCalendar-weekDayLabel': {
                                fontWeight: 900,
                                color: '#6b7280',
                                fontSize: 11,
                                textTransform: 'none',
                            },

                            '& .MuiPickersDay-root': {
                                borderRadius: 10,
                                fontWeight: 750,
                            },
                            '& .MuiPickersDay-root.Mui-selected': {
                                background: '#087bbd',
                            },
                            '& .MuiPickersDay-root.Mui-selected:hover': {
                                background: '#096ba5',
                            },
                            '& .MuiPickersDay-root:not(.Mui-selected):hover': {
                                backgroundColor: 'rgba(8, 123, 189, 0.08)',
                            },

                            '& .MuiPickersLayout-actionBar': {
                                px: 1.25,
                                py: 1,
                                borderTop: '1px solid #e5e7eb',
                            },
                        },
                    },
                    actionBar: { actions: ['clear', 'today'] },
                }}
            />
        </LocalizationProvider>
    );
}
